<template>
  <div
    class="option program-tile-btn"
    @click="clicked"
    :class="{ single: singleRateTypeAvailable }"
  >
    <div class="header">
      <p>
        {{ ratePlanOption.year }} Year Rates & Estimated Payments {{ paymentCalculationErrorText }}
      </p>
      <p v-if="promoText">{{ promoText }}</p>
    </div>
    <div class="content">
      <div class="rate fixedRate" v-if="ratePlanOption.hasFixedRate">
        <refi-program-loan-option-details
          :rateType="RateTypeNames.FIXED"
          :rate-term="ratePlanOption.year"
          :option="fixedRateOption"
          :singleRateTypeAvailable="singleRateTypeAvailable"
          :singleRateOptionClicked="singleRateOptionClicked"
          @radioButtonClicked="updatePaymentOption(fixedRateOption, RateTypeNames.FIXED)"
        />
      </div>
      <div class="rate variableRate" v-if="ratePlanOption.hasVariableRate">
        <refi-program-loan-option-details
          :rateType="RateTypeNames.VARIABLE"
          :rate-term="ratePlanOption.year"
          :option="variableRateOption"
          :singleRateTypeAvailable="singleRateTypeAvailable"
          @radioButtonClicked="updatePaymentOption(variableRateOption, RateTypeNames.VARIABLE)"
          :singleRateOptionClicked="singleRateOptionClicked"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { defineProps, withDefaults, computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { RateTypeNames } from "@/config/constants";
import { RefiRatePlanViewModel } from "@/models/refi";
import RefiProgramLoanOptionDetails from "@/components/refi-loan-configuration/RefiProgramLoanOptionDetails.vue";

interface Props {
  ratePlanOption: RefiRatePlanViewModel;
  header?: string;
}

const props = withDefaults(defineProps<Props>(), {
  header: "",
});

const store = useStore();

const selectedRatePlan = ref<any | undefined>(undefined);
const singleRateOptionClicked = ref<boolean>(false);

const fixedPaymentCalculationErrorText = computed(
  () => props.ratePlanOption?.estimatedPaymentFixedStatus,
);

const variablePaymentCalculationErrorText = computed(
  () => props.ratePlanOption?.estimatedPaymentVariableStatus,
);

const paymentCalculationError = computed(
  () =>
    (fixedPaymentCalculationErrorText.value &&
      fixedPaymentCalculationErrorText.value?.length > 0) ||
    (variablePaymentCalculationErrorText.value &&
      variablePaymentCalculationErrorText.value?.length > 0),
);

const paymentCalculationErrorText = computed(() =>
  paymentCalculationError.value ? "(Currently Unavailable)" : "",
);

const fixedRateOption = computed(() => props.ratePlanOption["fixedRate"]);

const variableRateOption = computed(() => props.ratePlanOption["variableRate"]);

const selectedRatePlanId = computed(
  () => store.state.refiLoanConfigurationModule.selectedRatePlanId,
);
const singleRateTypeAvailable = computed(
  () =>
    (props.ratePlanOption?.hasFixedRate && !props.ratePlanOption?.hasVariableRate) ||
    (!props.ratePlanOption?.hasFixedRate && props.ratePlanOption?.hasVariableRate),
);

const promoText = computed(() => {
  return props.ratePlanOption.greaterThan10YearPromoDisclaimerText
    ? props.ratePlanOption.greaterThan10YearPromoDisclaimerText?.toUpperCase()
    : "";
});

function clicked() {
  if (singleRateTypeAvailable.value) {
    singleRateOptionClicked.value = true;
  }
}
async function updatePaymentOption(payment: any, rateType?: string) {
  selectedRatePlan.value = props.ratePlanOption;

  await store.dispatch(
    "refiLoanConfigurationModule/setSelectedRateTerm",
    props.ratePlanOption.year,
  );
  await store.dispatch("refiLoanConfigurationModule/setSelectedRateOption", payment);
  await store.dispatch("refiLoanConfigurationModule/setSelectedRatePlanId", payment?.rateId);
  await store.dispatch("refiLoanConfigurationModule/setSelectedRateType", rateType);

  singleRateOptionClicked.value = false;
}

watch(
  () => selectedRatePlanId.value,
  (currentValue: boolean) => {
    if (currentValue) {
      singleRateOptionClicked.value = false;
    }
  },
);
</script>
<style lang="scss" scoped>
.hide {
  display: none;
}
hr.divider {
  transform: rotate(180deg);
  border: 1px solid var(--theme-color);
}
.program-tile-btn {
  font-family: var(--body-font);
  font-weight: 400;
  padding: 0.75rem;
  display: flex;
  min-width: 120px;

  min-height: 102px;
  box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
  -webkit-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
  -moz-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  margin-bottom: 1.5rem;
  border: solid 1px var(--theme-color);
  &.single {
    min-height: 64px;
  }
  &.active {
    box-shadow: 0px 3px 5px 4px rgba(0, 0, 0, 0.25);
    -webkit-box-shadow: 0px 3px 15px rgba(var(--theme-color-rgb), 0.1);
    -moz-box-shadow: 0px 3px 15px rgba(var(--theme-color-rgb), 0.1);
    border: 5px solid var(--theme-color);

    .card-content,
    .card-header {
      font-weight: 600;
    }
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0px 3px 25px rgba(0, 0, 0, 0.2);
    -webkit-box-shadow: 0px 3px 25px rgba(0, 0, 0, 0.2);
    -moz-box-shadow: 0px 3px 25px rgba(0, 0, 0, 0.2);
  }
}

.option {
  display: flex;
  flex-direction: column;
}

.option .header {
  font-family: var(--header-font);
  font-size: 20px;
  font-weight: 600;
  text-align: left;
  margin-bottom: 0.5em;
}

.option .content {
  display: flex;
  flex-direction: row;
  gap: 1em;
}

.option .content .fixedRate,
.option .content .variableRate {
  display: flex;
  flex-direction: row;
  gap: 1em;
}

.option .rate:first-child {
  padding-right: 1em;
  border-right: 1px solid var(--theme-color);
}

.option.single .rate:first-child {
  padding-right: 0;
  border-right: none;
}

.content {
  &.showDivider {
    .fixedRates {
      border-right: 1px solid var(--theme-color);
      padding-right: 2em;
    }
  }
}

.ratePercent {
  border-right: 1px solid var(--theme-color);
  padding-right: 1em;
  display: flex;
  align-items: center;
}

.monthlyPaymentAmount {
  display: flex;
  flex-direction: column;
}

.monthlyPaymentAmount p {
  margin: 0;
}

@media (max-width: 992px) {
  .program-tile-btn {
    height: fit-content;
    max-height: 375px;
    padding: 0.875rem;
    min-width: -webkit-fill-available;
    max-width: -webkit-fill-available;
  }
}
@media (max-width: 575px) {
  .program-tile-btn {
    max-height: none;
  }
  .option .rate:first-child {
    border: none;
  }
  .option .content {
    flex-direction: column;
    gap: 0rem;

    &.showDivider {
      .fixedRates {
        border-right: none;
        padding-right: 0;
      }
    }
  }
}
@media (max-width: 375px) {
  .program-tile-btn {
    height: fit-content;
    max-height: none;
    padding: 0.875rem;
  }
  .options .content {
    flex-direction: column;
    gap: 0em;
  }
}
</style>
